import { useQuery } from '@tanstack/react-query';

import { warehouseApi } from 'api/warehouse';
import { ApiError } from 'shared/errors/ApiError';

export const BULK_MOVE_ASYNC_POLL_INTERVAL_MS = 3000;
export const BULK_MOVE_PALLETS_ASYNC_STATUS_QUERY_KEY = 'bulkMovePalletsAsyncStatus';

export const bulkMovePalletsAsyncStatusQueryKeys = {
    all: [BULK_MOVE_PALLETS_ASYNC_STATUS_QUERY_KEY],
    byBatchId: (batchId) => [BULK_MOVE_PALLETS_ASYNC_STATUS_QUERY_KEY, batchId],
};

/**
 * Network and most server failures are retriable. Stop for expired batch (404) or auth errors.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isRetriableBulkMovePollError(error) {
    if (!error) {
        return false;
    }
    const { statusCode } = error;
    if (statusCode === 404 || statusCode === 401 || statusCode === 403) {
        return false;
    }
    return true;
}

function shouldContinuePolling(query) {
    if (query.state.data?.status === 'completed') {
        return false;
    }
    if (query.state.error && !isRetriableBulkMovePollError(query.state.error)) {
        return false;
    }
    return true;
}

/**
 * Polls async bulk pallet move progress until the batch completes or hits a terminal error.
 *
 * @param {Object} options
 * @param {string|null|undefined} options.batchId
 * @param {boolean} [options.enabled=true]
 */
export function useBulkMovePalletsAsyncStatusQuery({ batchId, enabled = true } = {}) {
    return useQuery({
        enabled: Boolean(enabled && batchId),
        queryKey: bulkMovePalletsAsyncStatusQueryKeys.byBatchId(batchId),
        retry: false,
        // Avoid focus refetch after completion (can 404 an expired Redis batch).
        refetchOnWindowFocus: false,
        // Resume after brief connectivity loss while the batch is still running.
        refetchOnReconnect: (query) => query.state.data?.status !== 'completed',
        refetchInterval: (query) => (shouldContinuePolling(query) ? BULK_MOVE_ASYNC_POLL_INTERVAL_MS : false),
        queryFn: async ({ signal }) => {
            const response = await warehouseApi.getBulkMovePalletsAsyncStatus(batchId, { signal });
            if (!response?.status) {
                throw new ApiError(
                    response?.msg || 'This move batch was not found or has expired. Please try again.',
                    response?.statusCode,
                    response?.data,
                );
            }
            return response.data;
        },
    });
}
